import { useState } from 'react';
import SafeIcon from './SafeIcon';
import './setup.css';
import './welcome.css';
import ConnectSheet from './ConnectSheet';
import { useApp } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { useDemo, useWorkspaceData } from '../store/DemoStore';
import { seedAssets } from '../store/seed';
import {
  CARDS,
  CAPTION_NAMES,
  DEMO_LOGO,
  DEMO_SITE,
  LIKE_CHIPS,
  LOOKS,
  STAGES,
  captionSummary,
  coloursFromImage,
  readWebsite,
  sampleCaption,
  toneExamples,
} from '../store/welcome';
import type { WelcomeDraft } from '../store/welcome';
import type { Channel } from '../types';

const DEMO_WORKSPACE_ID = 'brzoza';
const DEMO_PHOTO_COUNT = 4;
const DEFAULT_BRAND_COLOUR = '#4F7A5A';
const DEFAULT_LIGHT_COLOUR = '#F4EDE4';
const LOOK_WITH_TEXT_BAND = 2;
const TONE_FOR_EACH_LOOK = ['warm', 'clean', 'playful'] as const;

const demoPhotos = seedAssets
  .filter(
    (asset) => asset.workspaceId === DEMO_WORKSPACE_ID && asset.image && asset.kind === 'Photo'
  )
  .slice(0, DEMO_PHOTO_COUNT);
const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(file);
  });

export default function WelcomeFlow() {
  const { translate, language, readOnly } = useApp();
  const { route, navigate, actor, notify } = useUi();
  const { commands } = useDemo();
  const { welcome: draft, accounts, workspace } = useWorkspaceData(route.workspaceId);
  const [connectSheetOpen, setConnectSheetOpen] = useState(false);
  const [mix, setMix] = useState<{ look: number; caption: number }>({ look: 0, caption: 0 });
  const [noneOfTheseOpen, setNoneOfTheseOpen] = useState(false);
  const [noneOfTheseFeedback, setNoneOfTheseFeedback] = useState('');
  const [postLink, setPostLink] = useState('');
  const [drivePickerOpen, setDrivePickerOpen] = useState(false);
  const interfaceLanguage: 'pl' | 'en' = language === 'pl' ? 'pl' : 'en';
  const save = (patch: Partial<WelcomeDraft>) => commands.saveWelcome(workspace.id, patch);
  const profiles = accounts.filter((account) => account.health === 'Connected');
  const sampleLanguage: 'pl' | 'en' =
    draft.language === 'pl' || draft.language === 'en'
      ? draft.language
      : (draft.sampleLanguage ?? interfaceLanguage);
  const channel: Channel = profiles[0]?.network ?? 'Instagram';
  const visibleCards = CARDS.filter((candidate) => candidate.id !== 'likes' || draft.photo);
  const cardIndex = Math.max(
    0,
    visibleCards.findIndex((candidate) => candidate.id === draft.card)
  );
  const card = visibleCards[cardIndex] ?? visibleCards[0];
  const moveByCards = (delta: number) => {
    if (card.id === 'name' && draft.name.trim())
      commands.saveBusiness(workspace.id, { name: draft.name.trim() }, actor);
    const targetIndex = Math.min(visibleCards.length - 1, Math.max(0, cardIndex + delta));
    save({ card: visibleCards[targetIndex].id });
    window.scrollTo({ top: 0 });
  };
  const image = draft.photo?.src ?? demoPhotos[0]?.image ?? '';
  const keptColours = draft.colours.filter((colour) => colour.keep).map((colour) => colour.hex);
  const brandColour = keptColours[0] ?? DEFAULT_BRAND_COLOUR;
  const lightColour = keptColours.find((_, keptIndex) => keptIndex > 0) ?? DEFAULT_LIGHT_COLOUR;

  if (draft.done)
    return (
      <div className="content-stack welcome-page">
        <div className="panel welcome-card welcome-done">
          <SafeIcon name="FiCheckCircle" size={36} />
          <h1>{translate('Your style is saved')}</h1>
          <p>
            {translate(
              'AMY will use it for every new post. Your first post is waiting for your review; nothing is scheduled until you approve it.'
            )}
          </p>
          <div className="button-row center">
            <button
              className="button primary"
              onClick={() => navigate({ page: 'post', id: draft.postId })}
            >
              <SafeIcon name="FiEye" />
              {translate('Review my first post')}
            </button>
            <button className="button" onClick={() => navigate({ page: 'ideas' })}>
              <SafeIcon name="FiCalendar" />
              {translate('Plan my first month')}
            </button>
          </div>
          <div className="welcome-left">
            <h2>{translate('Still to do, whenever you are ready')}</h2>
            <ul>
              {profiles.length === 0 && (
                <li>{translate('Connect a social profile before anything can be scheduled.')}</li>
              )}
              <li>{translate('Add opening hours and contact details.')}</li>
              <li>{translate('Add more photos to Materials.')}</li>
              <li>
                {translate(
                  'Connect your own AI account. Until then AMY drafts on Hakusana’s trial allowance.'
                )}
              </li>
            </ul>
            <button className="button small" onClick={() => navigate({ page: 'onboarding' })}>
              <SafeIcon name="FiList" />
              {translate('Open the setup checklist')}
            </button>
          </div>
        </div>
      </div>
    );

  const optional = !card.required && card.id !== 'summary' && card.id !== 'samples';
  const canContinue = isCardAnswered();

  function isCardAnswered() {
    switch (card.id) {
      case 'name':
        return draft.name.trim().length > 1;
      case 'about':
        return draft.about.trim().length > 5;
      case 'topic':
        return draft.topicKind === 'intro' || draft.topic.trim().length > 2;
      case 'language':
        return !!draft.language && (draft.language !== 'both' || !!draft.sampleLanguage);
      default:
        return true;
    }
  }

  function readSite(url: string) {
    const found = readWebsite(url);
    save({
      website: url,
      websiteRead: true,
      noWebsite: false,
      name: draft.name || found.name,
      about: draft.about || (found.about ? found.about[interfaceLanguage] : ''),
      logo:
        draft.logo ??
        (found.logo
          ? { name: 'logo-from-website.svg', src: found.logo, fromSite: true }
          : undefined),
      colours: draft.colours.length
        ? draft.colours
        : (found.colours ?? []).map((hex) => ({ hex, keep: true })),
    });
  }
  async function applyLogo(name: string, src: string) {
    const extracted = await coloursFromImage(src);
    save({
      logo: { name, src },
      logoSkipped: false,
      colours: extracted.length ? extracted.map((hex) => ({ hex, keep: true })) : draft.colours,
    });
  }
  function updateColour(colourIndex: number, patch: Partial<WelcomeDraft['colours'][number]>) {
    save({
      colours: draft.colours.map((otherColour, otherIndex) =>
        otherIndex === colourIndex ? { ...otherColour, ...patch } : otherColour
      ),
    });
  }
  function firstPostTitle() {
    const introducesBusiness = draft.topicKind === 'intro' || !draft.topic.trim();
    if (!introducesBusiness) return draft.topic.trim();
    return sampleLanguage === 'pl' ? `Przedstawiamy się: ${draft.name}` : `Meet ${draft.name}`;
  }
  function finish(lookIndex: number, captionIndex: number) {
    save({
      look: lookIndex,
      caption: captionIndex,
      card: 'summary',
      summaryLook: LOOKS[lookIndex].summary[interfaceLanguage],
      summaryCaption: captionSummary(captionIndex, interfaceLanguage, sampleLanguage),
    });
  }

  const cardBody = () => {
    switch (card.id) {
      case 'website':
        return (
          <>
            <div className="welcome-row">
              <input
                className="text-field"
                type="url"
                inputMode="url"
                aria-label={translate('Website address')}
                placeholder={translate('e.g. www.yourbusiness.pl')}
                value={draft.website}
                onChange={(event) => save({ website: event.target.value, websiteRead: false })}
              />
              <button
                className="button primary"
                disabled={draft.website.trim().length < 4}
                onClick={() => readSite(draft.website)}
              >
                <SafeIcon name="FiSearch" />
                {translate('Read my website')}
              </button>
            </div>
            <button className="link-button small-print" onClick={() => readSite(DEMO_SITE.url)}>
              {translate('Demo: use {url}', { url: DEMO_SITE.url })}
            </button>
            {draft.websiteRead && (
              <div className="notice-banner ok">
                <SafeIcon name="FiCheckCircle" />
                <span>
                  {translate(
                    'AMY read {url} (simulated in this demo). It found: {found}. You can check everything on the next cards.',
                    {
                      url: draft.website,
                      found:
                        [
                          draft.name && translate('your name'),
                          draft.about && translate('a short description'),
                          draft.logo?.fromSite && translate('a logo'),
                          draft.colours.length &&
                            translate('{n} colours', { n: draft.colours.length }),
                        ]
                          .filter(Boolean)
                          .join(', ') || translate('only a name'),
                    }
                  )}
                </span>
              </div>
            )}
          </>
        );
      case 'name':
        return (
          <input
            className="text-field big"
            aria-label={translate('Business name')}
            value={draft.name}
            onChange={(event) => save({ name: event.target.value })}
            placeholder={translate('e.g. Kawiarnia Zielna')}
            autoFocus
          />
        );
      case 'about':
        return (
          <>
            <textarea
              className="text-field"
              rows={4}
              aria-label={translate('What your business does')}
              value={draft.about}
              onChange={(event) => save({ about: event.target.value })}
              placeholder={translate('e.g. We’re a café in Warsaw that roasts its own coffee.')}
            />
            {draft.websiteRead && draft.about && (
              <p className="muted small-print">
                {translate('Taken from your website. Edit anything that is not right.')}
              </p>
            )}
          </>
        );
      case 'language':
        return (
          <>
            <div className="choice-grid">
              {(
                [
                  ['pl', 'Polish'],
                  ['en', 'English'],
                  ['both', 'Both'],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  className={`choice ${draft.language === id ? 'selected' : ''}`}
                  aria-pressed={draft.language === id}
                  onClick={() => save({ language: id })}
                >
                  {translate(label)}
                </button>
              ))}
            </div>
            {draft.language === 'both' && (
              <>
                <p>{translate('Which one for the first sample posts?')}</p>
                <div className="choice-grid">
                  {(
                    [
                      ['pl', 'Polish'],
                      ['en', 'English'],
                    ] as const
                  ).map(([id, label]) => (
                    <button
                      key={id}
                      className={`choice ${draft.sampleLanguage === id ? 'selected' : ''}`}
                      aria-pressed={draft.sampleLanguage === id}
                      onClick={() => save({ sampleLanguage: id })}
                    >
                      {translate(label)}
                    </button>
                  ))}
                </div>
              </>
            )}
          </>
        );
      case 'logo':
        return (
          <>
            {draft.logo && (
              <div className="logo-preview">
                <img src={draft.logo.src} alt={translate('Your logo')} />
                <span className="muted small-print">
                  {draft.logo.fromSite ? translate('From your website') : draft.logo.name}
                </span>
              </div>
            )}
            <div className="button-row">
              <label className="button">
                <SafeIcon name="FiUpload" />
                {translate(draft.logo ? 'Choose a different file' : 'Choose a file')}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={async (event) => {
                    const chosenFile = event.target.files?.[0];
                    if (chosenFile)
                      await applyLogo(chosenFile.name, await readFileAsDataUrl(chosenFile));
                  }}
                />
              </label>
              {!draft.logo && (
                <button
                  className="button ghost"
                  onClick={() => applyLogo('demo-logo.svg', DEMO_LOGO)}
                >
                  {translate('Demo: use a sample logo')}
                </button>
              )}
            </div>
          </>
        );
      case 'colours':
        return (
          <>
            {draft.colours.length > 0 ? (
              <div className="swatch-edit">
                {draft.colours.map((colour, colourIndex) => (
                  <div key={colourIndex} className={`swatch-item ${colour.keep ? '' : 'off'}`}>
                    <label
                      className="swatch big"
                      style={{ background: colour.hex }}
                      title={translate('Change')}
                    >
                      <input
                        type="color"
                        value={colour.hex}
                        aria-label={translate('Change colour {n}', { n: colourIndex + 1 })}
                        onChange={(event) =>
                          updateColour(colourIndex, { hex: event.target.value.toUpperCase() })
                        }
                      />
                    </label>
                    <button
                      className={`button small ${colour.keep ? 'primary' : ''}`}
                      aria-pressed={colour.keep}
                      onClick={() => updateColour(colourIndex, { keep: !colour.keep })}
                    >
                      {translate(colour.keep ? 'Keep' : 'Not this one')}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="muted">
                {translate(
                  'No logo or website colours yet. Pick a colour you always use, or let AMY choose.'
                )}
              </p>
            )}
            <div className="button-row">
              <label className="button small">
                <SafeIcon name="FiPlus" size={14} />
                {translate('Add a colour')}
                <input
                  type="color"
                  hidden
                  onChange={(event) =>
                    save({
                      colours: [
                        ...draft.colours,
                        { hex: event.target.value.toUpperCase(), keep: true },
                      ],
                    })
                  }
                />
              </label>
              {draft.colours.length === 0 && (
                <button
                  className={`button small ${draft.coloursByAmy ? 'primary' : ''}`}
                  onClick={() => save({ coloursByAmy: true })}
                >
                  {translate('Let AMY choose')}
                </button>
              )}
            </div>
          </>
        );
      case 'photo':
        return (
          <>
            {draft.photo && (
              <div className="photo-preview">
                <img src={draft.photo.src} alt="" />
                <span className="muted small-print">{draft.photo.name}</span>
              </div>
            )}
            <div className="choice-grid">
              <label className="choice">
                <SafeIcon name="FiSmartphone" />
                {translate('From my phone or computer')}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={async (event) => {
                    const chosenFile = event.target.files?.[0];
                    if (chosenFile)
                      save({
                        photo: {
                          name: chosenFile.name,
                          src: await readFileAsDataUrl(chosenFile),
                          from: 'phone',
                        },
                        photoSkipped: false,
                      });
                  }}
                />
              </label>
              <button className="choice" onClick={() => setDrivePickerOpen(!drivePickerOpen)}>
                <SafeIcon name="FiFolder" />
                {translate('From Google Drive or a shared folder')}
              </button>
              <div className="choice post-link">
                <SafeIcon name="FiLink" />
                <input
                  className="text-field"
                  aria-label={translate('Link to a post you like')}
                  placeholder={translate('Link to a post you like')}
                  value={postLink}
                  onChange={(event) => setPostLink(event.target.value)}
                />
                <button
                  className="button small"
                  disabled={postLink.trim().length < 8}
                  onClick={() =>
                    save({
                      photo: {
                        name: postLink.trim(),
                        src: demoPhotos[1]?.image ?? '',
                        from: 'post',
                      },
                    })
                  }
                >
                  {translate('Use')}
                </button>
              </div>
            </div>
            {drivePickerOpen && (
              <div className="drive-pick">
                <p className="muted small-print">
                  {translate('Google Drive (simulated): choose a photo')}
                </p>
                <div className="thumb-row">
                  {demoPhotos.map((photo) => (
                    <button
                      key={photo.id}
                      className="thumb-button"
                      onClick={() => {
                        save({
                          photo: {
                            name: photo.file ?? photo.name,
                            src: photo.image,
                            from: 'drive',
                          },
                        });
                        setDrivePickerOpen(false);
                      }}
                    >
                      <img src={photo.image} alt={photo.name} />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        );
      case 'likes':
        return (
          <>
            {draft.photo && (
              <div className="photo-preview small">
                <img src={draft.photo.src} alt="" />
              </div>
            )}
            <div className="filter-row">
              {LIKE_CHIPS.map((likeChip) => (
                <button
                  key={likeChip}
                  className={`channel-chip ${draft.likes.includes(likeChip) ? 'selected' : ''}`}
                  aria-pressed={draft.likes.includes(likeChip)}
                  onClick={() =>
                    save({
                      likes: draft.likes.includes(likeChip)
                        ? draft.likes.filter((selectedChip) => selectedChip !== likeChip)
                        : [...draft.likes, likeChip],
                    })
                  }
                >
                  {translate(likeChip)}
                </button>
              ))}
            </div>
            <input
              className="text-field"
              aria-label={translate('In your own words')}
              placeholder={translate(
                'In your own words, e.g. “It looks warm and shows our real shop”'
              )}
              value={draft.likeText}
              onChange={(event) => save({ likeText: event.target.value })}
            />
          </>
        );
      case 'tone': {
        const exampleCaptions = toneExamples(draft.name, sampleLanguage);
        return (
          <>
            <div className="tone-list">
              {TONE_FOR_EACH_LOOK.map((id, toneIndex) => (
                <button
                  key={id}
                  className={`tone-option ${draft.tone === id ? 'selected' : ''}`}
                  aria-pressed={draft.tone === id}
                  onClick={() => save({ tone: id })}
                >
                  <span className="muted small-print">{translate(CAPTION_NAMES[toneIndex])}</span>
                  <span>{exampleCaptions[toneIndex]}</span>
                </button>
              ))}
            </div>
            <details className="tone-own" open={draft.tone === 'own'}>
              <summary>{translate('Paste a caption you like instead')}</summary>
              <textarea
                className="text-field"
                rows={2}
                value={draft.ownTone}
                onChange={(event) =>
                  save({
                    ownTone: event.target.value,
                    tone: event.target.value.trim() ? 'own' : draft.tone,
                  })
                }
                aria-label={translate('A caption you like')}
              />
            </details>
            <button
              className={`link-button ${draft.tone === 'later' ? 'selected' : ''}`}
              onClick={() => save({ tone: 'later' })}
            >
              {translate('Let me decide after seeing the samples')}
            </button>
            {draft.tone && draft.tone !== 'later' && (
              <label className="wizard-field">
                {translate('Is there anything I should avoid saying? (optional)')}
                <input
                  className="text-field"
                  value={draft.avoid}
                  onChange={(event) => save({ avoid: event.target.value })}
                  placeholder={translate('e.g. no prices, never “cheap”')}
                />
              </label>
            )}
          </>
        );
      }
      case 'profiles':
        return (
          <>
            {profiles.length > 0 && (
              <div className="profile-list">
                {profiles.map((profile) => (
                  <div key={profile.id} className="checklist-item">
                    <span className="agency-mark">
                      {profile.name.replace('@', '')[0]?.toUpperCase()}
                    </span>
                    <div style={{ flex: 1 }}>
                      <strong>{profile.name}</strong>
                      <div className="muted small-print">
                        {profile.network} · {translate(profile.type)}
                      </div>
                    </div>
                    <span className="status published">{translate('Connected')}</span>
                  </div>
                ))}
              </div>
            )}
            {profiles.length === 0 ? (
              <button
                className="button primary"
                onClick={() => setConnectSheetOpen(true)}
                disabled={readOnly}
              >
                <SafeIcon name="FiLink" />
                {translate('Connect a profile')}
              </button>
            ) : (
              <div className="button-row">
                <button
                  className="button"
                  onClick={() => setConnectSheetOpen(true)}
                  disabled={readOnly}
                >
                  <SafeIcon name="FiPlus" />
                  {translate('Add another profile')}
                </button>
                <button className="button primary" onClick={() => moveByCards(1)}>
                  {translate('That’s it for now')}
                </button>
              </div>
            )}
            {profiles.length === 0 && (
              <button
                className="link-button"
                onClick={() => {
                  save({ skipProfiles: true });
                  moveByCards(1);
                }}
              >
                {translate('Preview samples without connecting')}
              </button>
            )}
            {profiles.length === 0 && (
              <p className="muted small-print">
                {translate(
                  'You can preview and save your style first. A profile is needed before anything is scheduled.'
                )}
              </p>
            )}
          </>
        );
      case 'topic':
        return (
          <>
            <div className="choice-grid">
              {(
                [
                  ['product', 'A new product'],
                  ['event', 'An event'],
                  ['intro', 'Introduce my business'],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  className={`choice ${draft.topicKind === id ? 'selected' : ''}`}
                  aria-pressed={draft.topicKind === id}
                  onClick={() => save({ topicKind: id })}
                >
                  {translate(label)}
                </button>
              ))}
            </div>
            {draft.topicKind !== 'intro' && (
              <input
                className="text-field"
                aria-label={translate('What it is about')}
                value={draft.topic}
                onChange={(event) =>
                  save({ topic: event.target.value, topicKind: draft.topicKind ?? 'own' })
                }
                placeholder={translate(
                  draft.topicKind === 'event'
                    ? 'e.g. Live music on Friday evening'
                    : 'e.g. Our new apple cake'
                )}
              />
            )}
            {draft.topicKind === 'event' && (
              <input
                className="text-field"
                aria-label={translate('When is it?')}
                placeholder={translate('When is it? e.g. Friday 27 Nov, 19:00')}
                value={draft.eventDate ?? ''}
                onChange={(event) => save({ eventDate: event.target.value })}
              />
            )}
          </>
        );
      case 'samples':
        return (
          <>
            <p className="muted small-print">
              {translate('Same story three ways, in {lang} for {channel}.', {
                lang: translate(sampleLanguage === 'pl' ? 'Polish' : 'English'),
                channel,
              })}
              {!profiles.length && ` ${translate('Preview only: no profile connected yet.')}`}
              {draft.round > 0 &&
                ` ${translate('New set with your feedback: “{text}”', { text: draft.feedback[draft.round - 1] })}`}
            </p>
            <div className="sample-cards">
              {LOOKS.map((look) => {
                const caption = sampleCaption(draft, look.id, sampleLanguage);
                const matchesChosenTone = draft.tone === TONE_FOR_EACH_LOOK[look.id];
                return (
                  <article
                    key={look.id}
                    className={`sample-post look-${look.id}`}
                    style={{
                      ['--brand' as string]: brandColour,
                      ['--light' as string]: lightColour,
                    }}
                  >
                    <header>
                      <span className="muted small-print">
                        {translate('Sample {n}', { n: look.id + 1 })} ·{' '}
                        {translate('As on {channel}', { channel })}
                      </span>
                      <strong>{translate(look.name)}</strong>
                      {matchesChosenTone && (
                        <span className="tag">{translate('Closest to your tone')}</span>
                      )}
                    </header>
                    <div className="sample-media">
                      <img src={image} alt="" style={{ filter: look.filter }} />
                      {look.id === LOOK_WITH_TEXT_BAND && (
                        <span className="sample-band">
                          {draft.topicKind === 'intro' || !draft.topic ? draft.name : draft.topic}
                        </span>
                      )}
                      {draft.logo && <img className="sample-logo" src={draft.logo.src} alt="" />}
                    </div>
                    <p className="sample-caption">{caption}</p>
                    <button
                      className="button primary small"
                      onClick={() => finish(look.id, look.id)}
                    >
                      {translate('Use this sample')}
                    </button>
                  </article>
                );
              })}
            </div>
            <div className="panel inset mix">
              <strong>{translate('Or mix and match')}</strong>
              <div className="welcome-row">
                <label className="wizard-field">
                  {translate('Use this look')}
                  <select
                    className="select-field"
                    value={mix.look}
                    onChange={(event) => setMix({ ...mix, look: Number(event.target.value) })}
                  >
                    {LOOKS.map((lookOption) => (
                      <option key={lookOption.id} value={lookOption.id}>
                        {translate('Sample {n}', { n: lookOption.id + 1 })} ·{' '}
                        {translate(lookOption.name)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="wizard-field">
                  {translate('Use this caption style')}
                  <select
                    className="select-field"
                    value={mix.caption}
                    onChange={(event) => setMix({ ...mix, caption: Number(event.target.value) })}
                  >
                    {LOOKS.map((lookOption) => (
                      <option key={lookOption.id} value={lookOption.id}>
                        {translate('Sample {n}', { n: lookOption.id + 1 })} ·{' '}
                        {translate(CAPTION_NAMES[lookOption.id])}
                      </option>
                    ))}
                  </select>
                </label>
                <button className="button" onClick={() => finish(mix.look, mix.caption)}>
                  {translate('Use these')}
                </button>
              </div>
            </div>
            {!noneOfTheseOpen ? (
              <button className="link-button" onClick={() => setNoneOfTheseOpen(true)}>
                {translate('None of these')}
              </button>
            ) : (
              <div className="panel inset">
                <label className="wizard-field">
                  {translate('What would you like me to change?')}
                  <textarea
                    className="text-field"
                    rows={2}
                    value={noneOfTheseFeedback}
                    onChange={(event) => setNoneOfTheseFeedback(event.target.value)}
                    placeholder={translate('e.g. less formal, no emoji, show the coffee closer')}
                  />
                </label>
                <button
                  className="button primary small"
                  disabled={noneOfTheseFeedback.trim().length < 3}
                  onClick={() => {
                    save({
                      round: draft.round + 1,
                      feedback: [...draft.feedback, noneOfTheseFeedback.trim()],
                    });
                    setNoneOfTheseOpen(false);
                    setNoneOfTheseFeedback('');
                    notify(translate('AMY made a new set with your feedback.'));
                  }}
                >
                  {translate('Make a new set')}
                </button>
              </div>
            )}
          </>
        );
      case 'summary': {
        const lookIndex = draft.look ?? 0;
        const captionIndex = draft.caption ?? 0;
        return (
          <>
            <div className="summary-grid">
              <label className="wizard-field">
                {translate('Look')}
                <textarea
                  className="text-field"
                  rows={2}
                  value={draft.summaryLook ?? LOOKS[lookIndex].summary[interfaceLanguage]}
                  onChange={(event) => save({ summaryLook: event.target.value })}
                />
              </label>
              <label className="wizard-field">
                {translate('Captions')}
                <textarea
                  className="text-field"
                  rows={2}
                  value={
                    draft.summaryCaption ??
                    captionSummary(captionIndex, interfaceLanguage, sampleLanguage)
                  }
                  onChange={(event) => save({ summaryCaption: event.target.value })}
                />
              </label>
            </div>
            <dl className="activate-summary">
              <dt>{translate('Business')}</dt>
              <dd>
                {draft.name} · {draft.about}
              </dd>
              <dt>{translate('Colours')}</dt>
              <dd>
                {keptColours.length ? (
                  <span className="swatch-row inline">
                    {keptColours.map((hex) => (
                      <span key={hex} className="swatch" style={{ background: hex }} title={hex} />
                    ))}
                  </span>
                ) : (
                  translate(draft.coloursByAmy ? 'AMY chooses' : 'Not set yet')
                )}
              </dd>
              <dt>{translate('Logo')}</dt>
              <dd>
                {draft.logo ? (
                  <img className="logo-mini" src={draft.logo.src} alt="" />
                ) : (
                  translate('Not set yet')
                )}
              </dd>
              {draft.avoid && (
                <>
                  <dt>{translate('Never say')}</dt>
                  <dd>{draft.avoid}</dd>
                </>
              )}
              <dt>{translate('First post')}</dt>
              <dd>
                {translate('Sample {look} look with sample {cap} captions, for {channel}', {
                  look: lookIndex + 1,
                  cap: captionIndex + 1,
                  channel,
                })}
                {!profiles.length &&
                  ` · ${translate('connect {channel} before it can be scheduled', { channel })}`}
              </dd>
            </dl>
            <p className="muted small-print">
              {translate(
                'Saving puts your first post in Needs your review. Nothing is scheduled until you approve it.'
              )}
            </p>
            <div className="button-row">
              <button
                className="button primary"
                disabled={readOnly}
                onClick={() => {
                  commands.finishWelcome(
                    workspace.id,
                    {
                      look: draft.summaryLook ?? LOOKS[lookIndex].summary[interfaceLanguage],
                      captions:
                        draft.summaryCaption ??
                        captionSummary(captionIndex, interfaceLanguage, sampleLanguage),
                      caption: sampleCaption(draft, captionIndex, sampleLanguage),
                      image,
                      channel,
                      language: sampleLanguage,
                      title: firstPostTitle(),
                      tone: CAPTION_NAMES[captionIndex],
                    },
                    actor
                  );
                  notify(translate('Style saved. Your first post is waiting for your review.'));
                  window.scrollTo({ top: 0 });
                }}
              >
                <SafeIcon name="FiCheck" />
                {translate('Save my style')}
              </button>
              <button className="button" onClick={() => save({ card: 'samples' })}>
                {translate('Back to the samples')}
              </button>
            </div>
          </>
        );
      }
      default:
        return null;
    }
  };

  const questions: Record<string, [string, string?]> = {
    website: [
      'Does your business have a website?',
      'I can read it and fill in what I find, so you only check it.',
    ],
    name: ['What should I call your business?'],
    about: [
      'What does your business do?',
      'One or two sentences, as you would tell a new customer.',
    ],
    language: ['What language should I write your posts in?'],
    logo: ['Do you have a logo you’d like me to use?'],
    colours: [
      'Which colours should I keep?',
      'Suggested from your logo or website. Tap a colour to change it.',
    ],
    photo: [
      'Can you show me a photo or post with a look you like?',
      'A phone photo of your place is perfect.',
    ],
    likes: ['What do you like about this look?', 'Pick any that fit, or say it in your own words.'],
    tone: ['Which of these sounds most like your business?'],
    profiles: [
      profiles.length
        ? 'Want to add another profile?'
        : 'Where would you like your first post to go?',
      profiles.length
        ? 'You can add more later in Settings.'
        : 'Connect one profile now. You can add more later.',
    ],
    topic: ['What should your first post be about?'],
    samples: ['Which sample feels most like your business?'],
    summary: [
      'Here’s what AMY will remember',
      'Edit anything before saving. You can change it later on the Brand page.',
    ],
  };
  const [question, hint] = questions[card.id];
  const stageProgressClass = (stageIndex: number) => {
    if (stageIndex < card.stage) return 'done';
    if (stageIndex === card.stage) return 'current';
    return '';
  };

  return (
    <div className="content-stack welcome-page">
      <div className="welcome-top">
        <ol className="welcome-stages" aria-label={translate('Progress')}>
          {STAGES.map((stage, stageIndex) => (
            <li
              key={stage}
              className={stageProgressClass(stageIndex)}
              aria-current={stageIndex === card.stage ? 'step' : undefined}
            >
              {translate(stage)}
            </li>
          ))}
        </ol>
        <button
          className="link-button"
          onClick={() => {
            notify(translate('Saved. Continue any time from the calendar.'));
            navigate({ page: 'calendar' });
          }}
        >
          {translate('Save and exit')}
        </button>
      </div>
      <section className="panel welcome-card" aria-labelledby="welcome-q">
        <div className="welcome-amy">
          <span className="amy-avatar" aria-hidden="true">
            A
          </span>
          <div>
            <span className="muted small-print">
              {translate('Question {n} of {total}', {
                n: cardIndex + 1,
                total: visibleCards.length,
              })}
              {card.required ? ` · ${translate('needed')}` : ''}
            </span>
            <h1 id="welcome-q">{translate(question)}</h1>
            {hint && <p className="muted">{translate(hint)}</p>}
          </div>
        </div>
        <div className="welcome-body">{cardBody()}</div>
        {card.id !== 'summary' && card.id !== 'samples' && (
          <div className="welcome-footer">
            <button
              className="button ghost"
              disabled={cardIndex === 0}
              onClick={() => moveByCards(-1)}
            >
              <SafeIcon name="FiArrowLeft" />
              {translate('Back')}
            </button>
            <span className="spacer" />
            {optional && card.id !== 'profiles' && (
              <button
                className="button"
                onClick={() => {
                  if (card.id === 'website') save({ noWebsite: true });
                  if (card.id === 'logo') save({ logoSkipped: true });
                  if (card.id === 'photo') save({ photoSkipped: true });
                  moveByCards(1);
                }}
              >
                {translate(card.id === 'website' ? 'No website' : 'Skip for now')}
              </button>
            )}
            {card.id !== 'profiles' && (
              <button
                className="button primary"
                disabled={!canContinue}
                onClick={() => moveByCards(1)}
              >
                {translate('Continue')}
                <SafeIcon name="FiArrowRight" />
              </button>
            )}
          </div>
        )}
        {card.id === 'samples' && (
          <div className="welcome-footer">
            <button className="button ghost" onClick={() => moveByCards(-1)}>
              <SafeIcon name="FiArrowLeft" />
              {translate('Back')}
            </button>
          </div>
        )}
      </section>
      {connectSheetOpen && <ConnectSheet onClose={() => setConnectSheetOpen(false)} />}
    </div>
  );
}
